<template>
  <div>
    <!-- Barre de progression durée -->
    <v-progress-linear
      v-if="recording"
      class="mb-4"
      color="red"
      height="6"
      :model-value="progressPercent"
      rounded
    />

    <div class="d-flex align-center ga-3 flex-wrap">
      <!-- Bouton enregistrer / arrêter -->
      <v-btn
        v-if="!recording"
        color="red"
        :disabled="uploading"
        :prepend-icon="audioBlob ? 'mdi-replay' : 'mdi-microphone'"
        @click="startRecording"
      >
        {{ audioBlob ? 'Ré-enregistrer' : 'Enregistrer' }}
      </v-btn>

      <v-btn
        v-else
        color="grey-darken-1"
        :disabled="processing"
        prepend-icon="mdi-stop"
        @click="stopRecording"
      >
        <template v-if="processing">
          Traitement...
        </template>
        <template v-else>
          Arrêter ({{ formattedTime }})
        </template>
      </v-btn>

      <!-- Lecteur audio preview -->
      <audio v-if="audioUrl && !recording" ref="audioPlayer" controls :src="audioUrl" />

      <!-- Durée de validité -->
      <v-select
        v-if="audioBlob && !recording"
        v-model="expiresInDays"
        density="compact"
        hide-details
        :items="expirationOptions"
        label="Validité"
        style="max-width: 180px;"
        variant="outlined"
      />

      <!-- Bouton envoyer -->
      <v-btn
        v-if="audioBlob && !recording"
        color="primary"
        :loading="uploading"
        prepend-icon="mdi-send"
        @click="send"
      >
        Envoyer
      </v-btn>

      <!-- Bouton supprimer -->
      <v-btn
        v-if="audioBlob && !recording && !uploading"
        icon="mdi-delete"
        size="small"
        variant="text"
        @click="reset"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import lamejs from '@breezystack/lamejs'
  import { computed, onBeforeUnmount, ref, watch } from 'vue'
  import { deviceRepository } from '@/api/repository/device.repository.ts'
  import { ToastType, useToastStore } from '@/stores/toast.store.ts'
  import { notifyApiError } from '@/utils/error.utils.ts'

  const MAX_DURATION_S = 60
  const SAMPLE_RATE = 44_100
  const MP3_KBPS = 128

  const toastStore = useToastStore()

  const props = defineProps<{
    deviceId: string
  }>()

  const emit = defineEmits<{
    'uploaded': []
    'update:recording': [value: boolean]
  }>()

  const recording = ref(false)
  const uploading = ref(false)

  watch(recording, value => emit('update:recording', value))
  const expiresInDays = ref(3)

  const expirationOptions = [
    { title: '1 jour', value: 1 },
    { title: '2 jours', value: 2 },
    { title: '3 jours', value: 3 },
    { title: '5 jours', value: 5 },
    { title: '7 jours', value: 7 },
  ]

  const audioBlob = ref<Blob | null>(null)
  const audioUrl = ref<string | null>(null)
  const elapsedSeconds = ref(0)

  let mediaStream: MediaStream | null = null
  let audioContext: AudioContext | null = null
  let workletNode: AudioWorkletNode | null = null
  let timerInterval: ReturnType<typeof setInterval> | null = null

  // Nouveau tableau pour stocker l'audio brut avant le post-traitement
  let rawAudioChunks: Float32Array[] = []

  const progressPercent = computed(() => {
    const percent = elapsedSeconds.value / MAX_DURATION_S
    return percent * 100
  })

  const formattedTime = computed(() => {
    const s = elapsedSeconds.value
    const min = Math.floor(s / 60)
    const sec = s % 60
    const paddedSec = sec.toString().padStart(2, '0')

    return `${min}:${paddedSec}`
  })

  async function startRecording () {
    reset()

    try {
      mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: SAMPLE_RATE,
          autoGainControl: true,
          echoCancellation: true,
          noiseSuppression: true,
        },
      })

      audioContext = new AudioContext({
        sampleRate: SAMPLE_RATE,
      })

      const source = audioContext.createMediaStreamSource(mediaStream)
      await audioContext.audioWorklet.addModule('/pcm-worker.js')

      workletNode = new AudioWorkletNode(audioContext, 'pcm-processor')

      // On vide le tableau brut avant de commencer
      rawAudioChunks = []

      // On stocke simplement les données brutes
      workletNode.port.addEventListener('message', (event: MessageEvent<Float32Array>) => {
        // IMPORTANT: On fait une copie (new Float32Array) car le buffer
        // original de l'event peut être recyclé et écrasé par le navigateur
        rawAudioChunks.push(new Float32Array(event.data))
      })

      workletNode.port.start()
      source.connect(workletNode)
      workletNode.connect(audioContext.destination)

      recording.value = true
      elapsedSeconds.value = 0

      timerInterval = setInterval(() => {
        elapsedSeconds.value++

        if (elapsedSeconds.value >= MAX_DURATION_S) {
          stopRecording()
        }
      }, 1000)
    } catch {
      toastStore.notify('Impossible d\'accéder au microphone ou d\'initialiser l\'audio.', ToastType.Error)
      stopRecording()
    }
  }

  const processing = ref(false)

  function stopRecording () {
    if ((!recording.value && !mediaStream) || processing.value) {
      return
    }

    if (timerInterval) {
      clearInterval(timerInterval)
      timerInterval = null
    }

    if (workletNode) {
      workletNode.disconnect()
      workletNode = null
    }

    if (mediaStream) {
      const tracks = mediaStream.getTracks()
      for (const track of tracks) {
        track.stop()
      }
      mediaStream = null
    }

    if (audioContext) {
      audioContext.close()
      audioContext = null
    }

    // --- C'EST ICI QUE LA MAGIE OPÈRE ---
    // Si on a enregistré des données, on lance le pipeline de traitement
    if (rawAudioChunks.length > 0) {
      processing.value = true

      setTimeout(() => {
        try {
          // 1. Normalisation dynamique (Gain calculé sur la durée totale)
          const pcm16 = normalizeAndConvert(rawAudioChunks)

          // 2. Encodage MP3 final en une passe
          audioBlob.value = encodeToMP3(pcm16, SAMPLE_RATE, MP3_KBPS)

          // 3. Création de l'URL pour la lecture
          audioUrl.value = URL.createObjectURL(audioBlob.value)
        } catch (error) {
          console.error('Erreur lors de l\'encodage audio :', error)
          toastStore.notify('Erreur lors du traitement de l\'enregistrement', ToastType.Error)
        } finally {
          rawAudioChunks = []

          processing.value = false
          recording.value = false
        }
      }, 50)
    } else {
      recording.value = false
    }
  }

  async function send () {
    if (!audioBlob.value) {
      return
    }

    uploading.value = true

    try {
      await deviceRepository.upload(props.deviceId, audioBlob.value, expiresInDays.value)
      toastStore.notify('Message envoyé !', ToastType.Success)

      reset()
      emit('uploaded')
    } catch (error) {
      notifyApiError('Erreur lors de l\'envoi du message', error)
    } finally {
      uploading.value = false
    }
  }

  function reset () {
    if (audioUrl.value) {
      URL.revokeObjectURL(audioUrl.value)
    }
    audioBlob.value = null
    audioUrl.value = null
    rawAudioChunks = []
  }

  /**
   * Normalizes the raw audio to a target peak and converts it to 16-bit PCM
   */
  function normalizeAndConvert (audioChunks: Float32Array[]): Int16Array {
    let totalLength = 0
    for (const chunk of audioChunks) {
      totalLength = totalLength + chunk.length
    }

    const fullAudio = new Float32Array(totalLength)
    let offset = 0
    for (const chunk of audioChunks) {
      fullAudio.set(chunk, offset)
      offset = offset + chunk.length
    }

    let maxPeak = 0
    for (const element of fullAudio) {
      const absValue = Math.abs(element)
      if (absValue > maxPeak) {
        maxPeak = absValue
      }
    }

    const targetPeak = 0.95
    let gain = 1

    if (maxPeak > 0) {
      gain = targetPeak / maxPeak
    }

    const MAX_GAIN = 15
    if (gain > MAX_GAIN) {
      gain = MAX_GAIN
    }

    console.log(`[Audio Normalizer] Max Peak detected: ${maxPeak.toFixed(4)}`)
    console.log(`[Audio Normalizer] Applied Gain multiplier: ${gain.toFixed(2)}x`)

    const pcm16 = new Int16Array(fullAudio.length)
    for (const [i, element] of fullAudio.entries()) {
      let sample = element * gain
      sample = Math.tanh(sample)
      pcm16[i] = sample < 0 ? sample * 0x80_00 : sample * 0x7F_FF
    }

    return pcm16
  }

  /**
   * Encodes the normalized PCM data to MP3
   */
  function encodeToMP3 (pcmData: Int16Array, sampleRate: number, kbps: number): Blob {
    const mp3encoder = new lamejs.Mp3Encoder(1, sampleRate, kbps)
    const mp3Data: Uint8Array[] = []
    const sampleBlockSize = 1152

    for (let i = 0; i < pcmData.length; i = i + sampleBlockSize) {
      const sampleChunk = pcmData.subarray(i, i + sampleBlockSize)
      const mp3buf = mp3encoder.encodeBuffer(sampleChunk)

      if (mp3buf.length > 0) {
        mp3Data.push(mp3buf)
      }
    }

    const finalBuf = mp3encoder.flush()

    if (finalBuf.length > 0) {
      mp3Data.push(finalBuf)
    }

    return new Blob(mp3Data as BlobPart[], { type: 'audio/mpeg' })
  }

  onBeforeUnmount(() => {
    stopRecording()
    reset()
  })
</script>
